import { createFileRoute, Link } from "@tanstack/react-router";
import prima2 from "@/assets/pulizia-cotto-portico-prima-brescia.webp.asset.json";
import dopo2 from "@/assets/pulizia-cotto-portico-dopo-brescia.webp.asset.json";
import libera1 from "@/assets/pavimento-cotto-cortile-trattato-lombardia.webp.asset.json";
import { BeforeAfter } from "@/components/BeforeAfter";
import { PreventivoForm } from "@/components/PreventivoForm";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { ANNI_ESPERIENZA, ANNO_FONDAZIONE, EMAIL, TEL_ANTONIO } from "@/lib/site";

const URL_PAGINA = "https://artigianidelcotto.com/come-pulire-il-cotto";
const TITOLO = "Come pulire il cotto: guida pratica | Artigiani del Cotto";
const DESCRIZIONE =
  "Come pulire il pavimento in cotto senza rovinarlo: prodotti giusti, errori da evitare, macchie, cere vecchie e manutenzione. Guida di artigiani lombardi.";
const IMMAGINE = `https://artigianidelcotto.com${dopo2.url}`;

const passaggi = [
  {
    titolo: "1. Togli polvere e sabbia prima dell'acqua",
    testo:
      "La sabbia sul cotto funziona come carta vetrata: passata con lo straccio bagnato graffia la cera e apre la superficie allo sporco. Scopa o aspira sempre prima di lavare.",
  },
  {
    titolo: "2. Lava con acqua tiepida e detergente neutro",
    testo:
      "Il cotto è un laterizio poroso e alcalino: vuole un detergente neutro (pH 7), poca schiuma e uno straccio ben strizzato. Cambia l'acqua spesso, altrimenti si riporta in giro lo sporco sciolto.",
  },
  {
    titolo: "3. Asciuga o arieggia",
    testo:
      "L'acqua che ristagna nelle fughe risale sotto forma di aloni bianchi (efflorescenze). Dopo il lavaggio apri le finestre o passa un panno asciutto sulle zone più bagnate.",
  },
  {
    titolo: "4. Rinnova la protezione quando serve",
    testo:
      "Se l'acqua non perla più e viene assorbita, la protezione è finita: è il momento di rifare la cera (in interno) o il protettivo idrorepellente (in esterno), non di lavare più forte.",
  },
];

const trattamenti = [
  {
    titolo: "Cera in pasta o liquida (solo interno)",
    quando: "Cotto antico o steso a mano, in casa.",
    testo:
      "Nutre il laterizio e dà il tipico tono caldo un po' satinato. Va lucidata a macchina lenta e rifatta ogni 2-3 anni nelle zone di passaggio. Non si usa mai all'aperto: con pioggia e gelo si sfoglia in pochi mesi.",
  },
  {
    titolo: "Protettivo traspirante idro-oleorepellente",
    quando: "Cotto interno moderno e tutto il cotto esterno.",
    testo:
      "Penetra nei pori e respinge acqua e unto lasciando respirare il pavimento. Effetto naturale, senza film lucido: è la scelta giusta dove c'è umidità di risalita o rischio di efflorescenze.",
  },
  {
    titolo: "Deceratura prima di ogni nuovo trattamento",
    quando: "Cotto opaco a chiazze, appiccicoso o ingiallito.",
    testo:
      "Applicare cera o protettivo sopra strati vecchi non serve a niente: il prodotto non attacca e la chiazza resta. Prima si scioglie tutto il vecchio, si risciacqua e si asciuga, poi si tratta.",
  },
  {
    titolo: "Velo di posa e cotto nuovo mai trattato",
    quando: "Pavimenti appena posati o mai finiti.",
    testo:
      "Il residuo di malta va tolto con un disincrostante tamponato e neutralizzato subito, una volta sola. Solo dopo il cotto è pronto a ricevere cera o protettivo in modo uniforme.",
  },
];

const esternoPassaggi = [
  {
    titolo: "1. Riconosci un cotto non trattato",
    testo:
      "Butta un bicchiere d'acqua: se scurisce subito e viene assorbito, il cotto è nudo. È il caso di cortili, camminamenti e porticati dove non è mai stato messo nulla, oppure dove il vecchio protettivo è finito da anni.",
  },
  {
    titolo: "2. Togli muschi, alghe e patina verde",
    testo:
      "Spazzola con detergente specifico per muschi, lavorando anche nelle fughe dove l'umidità resta. Bassa pressione: l'idropulitrice a getto forte scava la superficie del laterizio e le fughe di sabbia e cemento.",
  },
  {
    titolo: "3. Sciogli gli aloni bianchi di sali",
    testo:
      "Le efflorescenze non si tolgono con l'acqua: serve un lavaggio deacidificante tamponato e risciacquato bene. Se l'umidità di risalita è forte torneranno, ma un protettivo traspirante le rallenta molto.",
  },
  {
    titolo: "4. Aspetta l'asciugatura completa",
    testo:
      "Un cotto esterno vuole almeno 24-48 ore di tempo asciutto prima del protettivo. Sigillare un pavimento umido significa chiudere l'acqua dentro: dopo il primo gelo compaiono macchie e sfogliature.",
  },
  {
    titolo: "5. Applica un protettivo traspirante antigelo",
    testo:
      "Due mani incrociate di idro-oleorepellente a effetto naturale, mai una cera o un prodotto filmante. Dopo, la manutenzione è un semplice lavaggio neutro stagionale.",
  },
];

const errori = [
  {
    q: "Candeggina e ammoniaca",
    a: "Scoloriscono il cotto, mangiano la cera e lasciano zone più chiare che poi non si recuperano.",
  },
  {
    q: "Acido muriatico",
    a: "Aggredisce le fughe e apre i pori della mattonella. Gli acidi si usano solo in fase di deceratura professionale, diluiti e neutralizzati subito.",
  },
  {
    q: "Vapore e idropulitrice in interno",
    a: "Il vapore scioglie la cera e spinge l'acqua nel laterizio: dopo qualche mese compaiono aloni e macchie scure.",
  },
  {
    q: "Detersivi per piatti e sgrassatori",
    a: "Lasciano un film appiccicoso che trattiene la polvere: il pavimento sembra sempre sporco poco dopo il lavaggio.",
  },
  {
    q: "Cere spray da supermercato",
    a: "Si accumulano a strati, ingialliscono e con il tempo vanno tolte tutte con la deceratura.",
  },
];

const macchie = [
  { tipo: "Unto e olio", cosa: "Impacco assorbente (terra di Sommières o talco) lasciato agire alcune ore, poi aspirato. Mai strofinare a caldo." },
  { tipo: "Vino, caffè, frutta", cosa: "Detergente neutro subito; se la macchia è vecchia serve un decerante locale e il ripristino della cera." },
  { tipo: "Aloni bianchi (sali)", cosa: "Sono efflorescenze da umidità: si tolgono a secco con spazzola, ma tornano finché non si risolve l'umidità di risalita." },
  { tipo: "Muschio e verde in esterno", cosa: "Detergente specifico e spazzolatura; poi protettivo idrorepellente per rallentarne il ritorno." },
  { tipo: "Cemento e velo di posa", cosa: "Serve un disincrostante professionale tamponato: è un intervento da fare una volta sola, all'inizio." },
  { tipo: "Ruggine di vasi e mobili", cosa: "Prodotti anti-ruggine dedicati; sul cotto antico meglio far provare prima un tecnico su una zona nascosta." },
];

const faq = [
  {
    q: "Con cosa si lava il pavimento in cotto tutti i giorni?",
    a: "Acqua tiepida e un detergente neutro per superfici cerate, straccio ben strizzato. Un cucchiaio di prodotto in un secchio basta: le dosi abbondanti lasciano un velo opaco.",
  },
  {
    q: "Si può usare l'aceto sul cotto?",
    a: "Meglio di no. L'aceto è acido: usato spesso opacizza la cera e con il tempo scava le fughe. Un uso isolatissimo e molto diluito non fa danni, ma non è la scelta giusta come detergente abituale.",
  },
  {
    q: "Come si toglie la cera vecchia dal cotto?",
    a: "Con una deceratura: si applica un decerante alcalino, si lascia agire, si spazzola a monospazzola e si aspira il residuo, risciacquando più volte. È un lavoro professionale: fatto male lascia macchie a chiazze.",
  },
  {
    q: "Ogni quanto va rifatta la cera?",
    a: "In casa, in zone di passaggio, ogni 2-3 anni; nelle stanze poco usate anche molto di più. Il segnale è semplice: se una goccia d'acqua viene assorbita invece di perlare, la protezione non c'è più.",
  },
  {
    q: "Il cotto esterno si pulisce come quello interno?",
    a: `No. All'aperto non si usa la cera ma un protettivo traspirante, e il lavaggio deve togliere muschi ed efflorescenze. Ne parliamo nella pagina dedicata al trattamento del cotto esterno.`,
  },
  {
    q: "Come si pulisce un pavimento in cotto esterno non trattato?",
    a: "Prima si spazzola via muschio e patina verde con un detergente specifico a bassa pressione, poi si fa un lavaggio deacidificante sugli aloni bianchi di sali. Dopo 24-48 ore di asciugatura completa si applica un protettivo traspirante antigelo: sul cotto nudo lavare senza proteggere significa ritrovarsi al punto di partenza dopo un inverno.",
  },
  {
    q: "Meglio la cera o il protettivo traspirante?",
    a: "La cera solo in interno, sul cotto antico, per il tono caldo e satinato. Il protettivo idro-oleorepellente traspirante in esterno e dove c'è umidità di risalita: respinge acqua e unto ma lascia respirare il laterizio, senza effetto plastica.",
  },
  {
    q: "Si può trattare il cotto d'inverno?",
    a: "Il lavaggio sì, il protettivo no se ci sono gelate o pioggia in arrivo: serve una finestra di due o tre giorni asciutti con temperature sopra i 5 °C. In sopralluogo ti diciamo se conviene aspettare la primavera.",
  },
  {
    q: "Quanto costa il trattamento di un pavimento in cotto?",
    a: "Dipende dai metri quadri, dallo stato del pavimento (cere vecchie, velo di posa, sali) e dalla finitura scelta. Facciamo sopralluogo e preventivo scritto gratuiti in tutta la Lombardia, senza impegno.",
  },
  {
    q: "Quando conviene chiamare un professionista?",
    a: "Quando il cotto è opaco a chiazze, ha cere ingiallite sovrapposte, macchie profonde o velo di posa mai rimosso. In quei casi lavare di più peggiora: serve un ciclo completo di deceratura, lavaggio e nuovo trattamento.",
  },
];

export const Route = createFileRoute("/come-pulire-il-cotto")({
  component: ComePulireIlCotto,
  head: () => ({
    meta: [
      { title: TITOLO },
      { name: "description", content: DESCRIZIONE },
      { property: "og:title", content: TITOLO },
      { property: "og:description", content: DESCRIZIONE },
      { property: "og:type", content: "article" },
      { property: "og:url", content: URL_PAGINA },
      { property: "og:image", content: IMMAGINE },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:image", content: IMMAGINE },
    ],
    links: [{ rel: "canonical", href: URL_PAGINA }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "HowTo",
              "@id": `${URL_PAGINA}#guida`,
              name: "Come pulire il pavimento in cotto",
              description: DESCRIZIONE,
              image: IMMAGINE,
              inLanguage: "it-IT",
              totalTime: "PT30M",
              supply: [
                { "@type": "HowToSupply", name: "Detergente neutro per cotto" },
                { "@type": "HowToSupply", name: "Acqua tiepida" },
              ],
              tool: [
                { "@type": "HowToTool", name: "Scopa o aspirapolvere" },
                { "@type": "HowToTool", name: "Straccio in microfibra" },
              ],
              step: passaggi.map((p, i) => ({
                "@type": "HowToStep",
                position: i + 1,
                name: p.titolo.replace(/^\d+\.\s*/, ""),
                text: p.testo,
                url: `${URL_PAGINA}#passaggi`,
              })),
            },
            {
              "@type": "Article",
              "@id": `${URL_PAGINA}#articolo`,
              headline: "Come pulire il cotto senza rovinarlo",
              description: DESCRIZIONE,
              image: IMMAGINE,
              inLanguage: "it-IT",
              mainEntityOfPage: URL_PAGINA,
              author: { "@type": "Organization", name: "Artigiani del Cotto", url: "https://artigianidelcotto.com/" },
              publisher: { "@id": "https://artigianidelcotto.com/#azienda" },
            },
            {
              "@type": "FAQPage",
              "@id": `${URL_PAGINA}#faq`,
              inLanguage: "it-IT",
              mainEntity: faq.map((f) => ({
                "@type": "Question",
                name: f.q,
                acceptedAnswer: { "@type": "Answer", text: f.a },
              })),
            },
            {
              "@type": "BreadcrumbList",
              "@id": `${URL_PAGINA}#briciole`,
              itemListElement: [
                { "@type": "ListItem", position: 1, name: "Home", item: "https://artigianidelcotto.com/" },
                { "@type": "ListItem", position: 2, name: "Come pulire il cotto", item: URL_PAGINA },
              ],
            },
          ],
        }),
      },
    ],
  }),
});

function ComePulireIlCotto() {
  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />

      <main>
        <section className="gradient-warm py-16 text-background sm:py-20">
          <div className="mx-auto max-w-6xl px-5">
            <nav aria-label="Percorso" className="text-sm text-background/70">
              <Link to="/" className="underline-offset-4 hover:underline">Home</Link>
              <span className="px-2">/</span>
              <span>Come pulire il cotto</span>
            </nav>
            <p className="eyebrow mt-6 text-sand">Guida pratica · artigiani dal {ANNO_FONDAZIONE}</p>
            <h1 className="mt-4 max-w-3xl text-4xl leading-[1.08] font-semibold sm:text-5xl">
              Come pulire il cotto senza rovinarlo
            </h1>
            <p className="mt-5 max-w-2xl text-base text-background/90 sm:text-lg">
              Quello che facciamo da {ANNI_ESPERIENZA} sui pavimenti dei nostri clienti, spiegato
              semplice: cosa usare ogni settimana, cosa non usare mai e quando invece il fai-da-te
              non basta più.
            </p>
          </div>
        </section>

        <section id="passaggi" className="mx-auto max-w-6xl scroll-mt-20 px-5 py-20">
          <p className="eyebrow">La pulizia ordinaria</p>
          <h2 className="mt-3 max-w-2xl text-3xl font-semibold sm:text-4xl">
            Quattro passaggi, ogni volta uguali.
          </h2>
          <ol className="mt-12 grid gap-6 sm:grid-cols-2">
            {passaggi.map((p) => (
              <li key={p.titolo} className="surface-warm rounded-sm border border-border p-7">
                <h3 className="text-lg font-semibold">{p.titolo}</h3>
                <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{p.testo}</p>
              </li>
            ))}
          </ol>
        </section>

        <section className="bg-secondary/40 py-20">
          <div className="mx-auto max-w-6xl px-5">
            <p className="eyebrow">Errori da evitare</p>
            <h2 className="mt-3 max-w-2xl text-3xl font-semibold sm:text-4xl">
              Cosa non mettere mai sul cotto.
            </h2>
            <div className="mt-10 grid gap-6 md:grid-cols-2">
              {errori.map((e) => (
                <article key={e.q} className="surface-warm rounded-sm border border-border p-6">
                  <h3 className="text-base font-semibold text-sienna">{e.q}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{e.a}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="mx-auto max-w-6xl px-5 py-20">
          <p className="eyebrow">Macchie</p>
          <h2 className="mt-3 max-w-2xl text-3xl font-semibold sm:text-4xl">
            Come si tratta ogni tipo di macchia.
          </h2>
          <div className="mt-10 overflow-x-auto">
            <table className="w-full min-w-[36rem] border-collapse text-left text-sm">
              <thead>
                <tr className="border-b border-border">
                  <th scope="col" className="py-3 pr-6 font-semibold">Macchia</th>
                  <th scope="col" className="py-3 font-semibold">Cosa fare</th>
                </tr>
              </thead>
              <tbody>
                {macchie.map((m) => (
                  <tr key={m.tipo} className="border-b border-border last:border-0">
                    <th scope="row" className="py-4 pr-6 align-top font-medium">{m.tipo}</th>
                    <td className="py-4 align-top text-muted-foreground">{m.cosa}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        <section id="trattamento" className="scroll-mt-20 bg-secondary/40 py-20">
          <div className="mx-auto max-w-6xl px-5">
            <p className="eyebrow">Il trattamento</p>
            <h2 className="mt-3 max-w-2xl text-3xl font-semibold sm:text-4xl">
              Trattare il cotto: cera o protettivo traspirante?
            </h2>
            <p className="mt-5 max-w-3xl text-base leading-relaxed text-muted-foreground">
              Pulire è solo metà del lavoro: un cotto senza protezione riassorbe sporco, unto e acqua
              nel giro di poche settimane. Il trattamento giusto dipende da dove si trova il
              pavimento e da cosa c'è sopra adesso.
            </p>
            <div className="mt-10 grid gap-6 md:grid-cols-2">
              {trattamenti.map((t) => (
                <article key={t.titolo} className="surface-warm rounded-sm border border-border p-7">
                  <h3 className="text-lg font-semibold">{t.titolo}</h3>
                  <p className="mt-2 text-xs font-medium tracking-wide text-sienna uppercase">{t.quando}</p>
                  <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{t.testo}</p>
                </article>
              ))}
            </div>
            <div className="mt-10 flex flex-wrap items-center gap-3">
              <a
                href="#contatti"
                className="rounded-xs bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground transition-colors hover:bg-sienna"
              >
                Richiedi un preventivo gratuito
              </a>
              <Link
                to="/pulizia-lavaggio-cotto"
                className="rounded-xs border border-border px-6 py-3 text-sm font-semibold transition-colors hover:border-primary"
              >
                Il nostro servizio di pulizia e lavaggio
              </Link>
            </div>
          </div>
        </section>

        <section id="cotto-esterno-non-trattato" className="mx-auto max-w-6xl scroll-mt-20 px-5 py-20">
          <p className="eyebrow">Cotto esterno non trattato</p>
          <h2 className="mt-3 max-w-2xl text-3xl font-semibold sm:text-4xl">
            Come pulire il cotto esterno mai protetto.
          </h2>
          <p className="mt-5 max-w-3xl text-base leading-relaxed text-muted-foreground">
            Cortili, camminamenti, scale e porticati lasciati nudi assorbono tutto: acqua, muschi,
            sali, foglie marce. Qui la cera non c'entra nulla e l'idropulitrice a tutta pressione fa
            più danni dello sporco. L'ordine dei passaggi conta più del prodotto.
          </p>
          <ol className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {esternoPassaggi.map((p) => (
              <li key={p.titolo} className="surface-warm rounded-sm border border-border p-7">
                <h3 className="text-base font-semibold">{p.titolo}</h3>
                <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{p.testo}</p>
              </li>
            ))}
          </ol>
          <div className="mt-10 rounded-sm border border-border p-7">
            <p className="text-base leading-relaxed">
              Se il cotto all'aperto è ancora nudo, il momento giusto per proteggerlo è subito dopo
              il lavaggio: è l'unico modo per non ripartire da zero dopo l'inverno.
            </p>
            <div className="mt-6 flex flex-wrap items-center gap-3">
              <Link
                to="/trattamento-cotto-esterno"
                className="rounded-xs bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground transition-colors hover:bg-sienna"
              >
                Trattamento cotto esterno
              </Link>
              <a
                href={`tel:${TEL_ANTONIO}`}
                className="rounded-xs border border-border px-6 py-3 text-sm font-semibold transition-colors hover:border-primary"
              >
                Chiama Antonio: 339 751 7813
              </a>
            </div>
            <p className="mt-5 text-sm text-muted-foreground">
              Lavori in provincia di Brescia o Bergamo?{" "}
              <Link to="/trattamento-pavimenti-cotto-brescia" className="text-primary underline underline-offset-4">
                Cotto a Brescia
              </Link>{" "}
              ·{" "}
              <Link to="/trattamento-pavimenti-cotto-bergamo" className="text-primary underline underline-offset-4">
                Superfici a Bergamo
              </Link>
            </p>
          </div>
        </section>

        <section id="cotto-fiorentino" className="scroll-mt-20 bg-secondary/40 py-20">
          <div className="mx-auto max-w-6xl px-5">
            <p className="eyebrow">Cotto fatto a mano</p>
            <h2 className="mt-3 max-w-2xl text-3xl font-semibold sm:text-4xl">
              Come pulire il cotto fiorentino e il cotto antico fatto a mano.
            </h2>
            <div className="mt-8 grid gap-8 lg:grid-cols-2">
              <div className="space-y-4 text-base leading-relaxed text-muted-foreground">
                <p>
                  Il cotto fiorentino, come tutto il cotto fatto a mano, ha una superficie irregolare e
                  molto più porosa di quella del cotto estruso moderno: assorbe acqua, olio e cera in
                  profondità, e proprio per questo va pulito con prudenza. Le spazzole troppo dure e i
                  disincrostanti concentrati consumano la pelle della mattonella e la lasciano polverosa.
                </p>
                <p>
                  Per la manutenzione quotidiana basta acqua tiepida e un detergente neutro, senza
                  ammoniaca né candeggina. Quando il pavimento è ingiallito o coperto da cera vecchia,
                  serve invece una deceratura completa seguita da un nuovo trattamento: è l'unico modo
                  per togliere gli strati opachi senza scavare il laterizio.
                </p>
                <p>
                  Nei pavimenti d'epoca capita spesso di trovare macchie di gesso, calce o velo di posa
                  mai rimosso. In quel caso usiamo un detergente acido tamponato a base d'acqua, diluito
                  e provato prima su una piastrella nascosta, e chiudiamo sempre con un protettivo
                  traspirante che lascia respirare il cotto.
                </p>
              </div>
              <ul className="surface-warm space-y-3 self-start rounded-sm border border-border p-7 text-sm">
                {[
                  "Mai aceto, anticalcare o candeggina: macchiano e schiariscono il cotto.",
                  "Niente vapore ad alta pressione sui pavimenti antichi: scalza la superficie.",
                  "Cera in pasta per gli interni, protettivo idro-oleorepellente per l'esterno.",
                  "Sul cotto fiorentino la prova su una piccola area è sempre il primo passaggio.",
                ].map((t) => (
                  <li key={t} className="flex gap-3">
                    <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-copper" />
                    <span>{t}</span>
                  </li>
                ))}
              </ul>
            </div>
            <p className="mt-8 text-sm text-muted-foreground">
              Hai un cotto fiorentino da recuperare?{" "}
              <Link to="/pulizia-lavaggio-cotto" className="text-primary underline underline-offset-4">
                Vedi il servizio di pulizia e lavaggio del cotto
              </Link>
              .
            </p>
          </div>
        </section>

        <section className="bg-secondary/40 py-20">
          <div className="mx-auto max-w-6xl px-5">
            <div className="grid items-center gap-10 md:grid-cols-2">
              <div>
                <p className="eyebrow">Quando serve un professionista</p>
                <h2 className="mt-3 text-3xl font-semibold sm:text-4xl">
                  Se lavando peggiora, il problema è la cera.
                </h2>
                <p className="mt-5 text-base leading-relaxed text-muted-foreground">
                  Un cotto opaco a chiazze, appiccicoso o ingiallito non è sporco: sono strati di
                  cere vecchie e detersivi che si sono sovrapposti negli anni. In quel caso serve una
                  deceratura completa, un lavaggio profondo e un nuovo trattamento — dopo, la
                  manutenzione di casa torna a bastare.
                </p>
                <div className="mt-8 flex flex-wrap gap-3">
                  <a
                    href="#contatti"
                    className="rounded-xs bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground transition-colors hover:bg-sienna"
                  >
                    Richiedi un preventivo gratuito
                  </a>
                  <a
                    href={`tel:${TEL_ANTONIO}`}
                    className="rounded-xs border border-border px-6 py-3 text-sm font-semibold transition-colors hover:border-primary"
                  >
                    Chiama Antonio
                  </a>
                </div>
                <p className="mt-6 text-sm">
                  <Link to="/trattamento-cotto-esterno" className="text-primary underline underline-offset-4">
                    Per cortili e terrazze: trattamento del cotto esterno
                  </Link>
                </p>
              </div>
              <BeforeAfter
                label="Portico in cotto decerato, lavato e trattato in provincia di Brescia"
                before={prima2.url}
                after={dopo2.url}
              />
            </div>
          </div>
        </section>

        <section className="mx-auto max-w-6xl px-5 py-20">
          <p className="eyebrow">Domande frequenti</p>
          <h2 className="mt-3 max-w-2xl text-3xl font-semibold sm:text-4xl">
            Quello che ci chiedono più spesso.
          </h2>
          <div className="mt-10 divide-y divide-border border-y border-border">
            {faq.map((f) => (
              <details key={f.q} className="group py-5">
                <summary className="cursor-pointer list-none text-base font-semibold marker:hidden">
                  {f.q}
                </summary>
                <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{f.a}</p>
              </details>
            ))}
          </div>
        </section>

        <section id="contatti" className="scroll-mt-20 bg-secondary/40 py-20">
          <div className="mx-auto grid max-w-6xl gap-12 px-5 md:grid-cols-2">
            <div>
              <p className="eyebrow">Preventivo</p>
              <h2 className="mt-3 text-3xl font-semibold sm:text-4xl">
                Non sai da dove partire? Mandaci due foto.
              </h2>
              <p className="mt-4 text-muted-foreground">
                Guardiamo lo stato del pavimento e ti diciamo se basta una manutenzione o serve un
                ciclo completo. Sopralluogo e preventivo gratuiti in tutta la Lombardia.
              </p>
              <img
                src={libera1.url}
                alt="Pavimento in cotto di un cortile lavato e trattato in Lombardia"
                width={900}
                height={675}
                loading="lazy"
                decoding="async"
                className="mt-8 aspect-4/3 w-full rounded-sm object-cover shadow-[var(--shadow-soft)]"
              />
              <p className="mt-6 text-sm text-muted-foreground">
                Oppure scrivici a{" "}
                <a href={`mailto:${EMAIL}`} className="text-primary underline underline-offset-4">
                  {EMAIL}
                </a>
                .
              </p>
            </div>
            <PreventivoForm />
          </div>
        </section>
      </main>

      <SiteFooter />
    </div>
  );
}
